import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { Project } from '../../../types';

type GetProjectParams = {
  ref_type?: string;
  ref?: string;
  badge_ref?: string;
};

function fetchProject(projectLocator: string, params?: GetProjectParams) {
  return get<Project>(
    `/api/projects/${nuc(projectLocator)}`,
    false,
    params,
    {
      encodeQuery: true, // ref may contain slashes and other special characters
    },
  );
}

export const getProjectQueryKey = (locator: string, params?: GetProjectParams): QueryKey => {
  if (params) {
    return ['projects', locator, params];
  }
  return ['projects', locator];
};

/**
 * Query for a single project by its locator.
 *
 * @param {string} projectLocator - The locator of the project to fetch. No need to call nuc before passing it in here.
 * @param {GetProjectParams} params - Optional query parameters (ref_type, ref, badge_ref).
 * @param {boolean} shouldFetch - Will not fetch if false.
 */
export default function useGetProject(
  projectLocator: string,
  params?: GetProjectParams,
  shouldFetch = true,
) {
  return useQuery<Project>({
    queryKey: getProjectQueryKey(projectLocator, params),
    queryFn: () => fetchProject(projectLocator, params),
    enabled: shouldFetch,
    staleTime: 1000 * 60, // 1 minute
  });
}
